/**
 * Ensures a valid, non-expired *transient* (anonymous) token is present in the
 * token manager and returns its access token string.
 *
 * If the current token is not a valid transient token (e.g. a stale member
 * token lingering after logout, or an expired transient token), the token
 * manager is cleared and a fresh transient token is acquired before returning.
 *
 * Clearing stays LOCAL (no endSession) so it does not poison the sign-in flow.
 *
 * @returns The transient access token string, or `undefined` when no auth
 *          client is available or a token could not be acquired.
 */
export const ensureTransientToken = async (): Promise<string | undefined> => {
  const authClient = window.mgm?.authClient;
  if (!authClient) return undefined;

  const token = await authClient.getAccessToken();
  const hasValidTransientToken =
    !!token?.accessToken && token.isTransient && (!token.expiresAt || token.expiresAt * 1000 > Date.now());

  if (hasValidTransientToken) {
    return token?.accessToken;
  }

  authClient.getAuthClient().tokenManager.clear();
  const freshToken = await authClient.acquireUserTokens(true);
  return freshToken?.accessToken;
};
